import {
	computeContextMenuTitle,
	ContextMenu,
	DateString,
	TableView,
	VirtualTableViewColumnDefinition,
	useContextMenu
} from "client/Components";
import { SortingFor } from "client/Data/sorting";
import {
	MultiSelectableOptions,
	SelectableItemOptions,
	useUserTimezone
} from "client/Hooks";
import { getDisplayFriendlySize } from "client_legacy/Documents/fileExtensions";
import {
	ReactElement,
	RefObject,
	useMemo,
	useRef
} from "react";
import { localize } from "client_legacy/ndaw/culture";
import { DocumentName, DocumentNameKind } from "../../DocumentName";
import {
	GridItem,
	RecycleBinAction,
	RecycleBinActionContext
} from "./SharePointRecycleBin";
import { TimeZone } from "client/DateAndTime/timezones";
import { AiAssistantFabSpacing } from "client/Features/ChatAssistant/AiAssistantFabSpacing";

export type SharePointRecycleBinTableViewProps = {
	items: readonly GridItem[];
	sortingFor: SortingFor<GridItem>;
	multiSelection: MultiSelectableOptions;
	selectionFor: (item: GridItem) => SelectableItemOptions;
	selectionActions: readonly RecycleBinAction[];
	actionContext: RecycleBinActionContext;
};

export const SharePointRecycleBinTableView = (props: SharePointRecycleBinTableViewProps): ReactElement => {
	const {
		items,
		sortingFor,
		multiSelection,
		selectionFor,
		selectionActions,
		actionContext
	} = props;

	const userTimezone: TimeZone = useUserTimezone();
	const tableRef: RefObject<HTMLDivElement | null> = useRef<HTMLDivElement>(null);
	const [contextMenuHandler, contextMenuProps] = useContextMenu(tableRef);

	const columns: readonly VirtualTableViewColumnDefinition[] = useColumnDefinitions(
		sortingFor
	);

	return (
		<>
			<TableView
				isVirtual
				hasNoBottomDelimiter
				columns={columns}
				items={items}
				height="fill"
				itemKey="id"
				trackingId="sharepoint-recyclebin-grid"
				multiSelectable={multiSelection}
				ref={tableRef}
				noResultConfig={{
					icon: "folder-open",
					title: localize("_NoDocumentsToShow"),
					message: localize("_ThereAreCurrentlyNoDocumentsInThisFolder")

				}}
				renderAfterItems={
					items.length > 0
						? <AiAssistantFabSpacing />
						: undefined
				}
			>
				{(x): ReactElement => (
					<TableView.Row
						selectable={selectionFor(x)}
						onContextMenu={contextMenuHandler}
						trackingId={x.id}
					>
						<TableView.Cell>
							<DocumentName
								name={x.name}
								documentKind={x.model.itemType}
								mode={{
									kind: DocumentNameKind.Default,
									hasBookmark: false
								}}
							/>
						</TableView.Cell>

						<TableView.Cell>
							<span title={x.originalLocation}>
								{x.originalLocation}
							</span>
						</TableView.Cell>

						<TableView.Cell>
							{x.deletedOn && (
								<DateString
									value={x.deletedOn}
									format="RelativeTimestamp"
									timezone={userTimezone}
								/>
							)}
						</TableView.Cell>

						<TableView.Cell>
							{getDisplayFriendlySize(x.size)}
						</TableView.Cell>
					</TableView.Row>
				)}
			</TableView>

			{contextMenuProps && (
				<ContextMenu
					{...contextMenuProps}
					actions={selectionActions}
					context={actionContext}
					trackingId="sharepoint-recyclebin-context-menu"
					title={computeContextMenuTitle(
						actionContext.selection,
						(x) => x.name
					)}
				/>
			)}
		</>
	);
};
SharePointRecycleBinTableView.displayName = "SharePointRecycleBinTableView";

function useColumnDefinitions(
	sortingFor: SortingFor<GridItem>
): readonly VirtualTableViewColumnDefinition[] {
	return useMemo(
		(): readonly VirtualTableViewColumnDefinition[] => {
			return [
				{
					title: localize("_Name"),
					sortable: sortingFor("name"),
					trackingId: "name",
					width: 400
				},
				{
					title: localize("_OriginalLocation"),
					sortable: sortingFor("originalLocation"),
					trackingId: "original-location",
					width: 400
				},
				{
					title: localize("_DateDeleted"),
					sortable: sortingFor("deletedOn"),
					trackingId: "deleted-on",
					width: 150
				},
				{
					title: localize("_FileSize"),
					sortable: sortingFor("size"),
					trackingId: "size",
					width: 150
				}
			];
		},
		[sortingFor]
	);
}